import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Modal,
	Row,
	Select,
	Table,
	Tag,
	Typography,
	notification,
} from "antd";
import { api } from "../utils/Api";
import {
	filterOption,
	getDisabledDate,
	getDisabledTime,
	getAvailableOptions,
	useShiftGroupOptions,
} from "./shiftGroupUtils";
const { Text } = Typography;

const formatDate = (value) => dayjs(value).format("MM/DD/YYYY hh:mm A");

const HISTORY_STATUS_COLORS = {
	Created: "green",
	Edited: "orange",
	"Shift Moved": "blue",
	"Moved Group": "purple",
};

const historyColumns = [
	{
		title: "#",
		key: "number",
		width: 50,
		render: (_, item, index) => index + 1,
	},
	{
		title: "Status",
		key: "status",
		render: (_, item) => (
			<Tag color={HISTORY_STATUS_COLORS[item.status]}>{item.status}</Tag>
		),
	},
	{
		title: "Shift Start",
		key: "startDate",
		render: (_, item) => formatDate(item.startDate),
	},
	{
		title: "Shift End",
		key: "endDate",
		render: (_, item) => formatDate(item.endDate),
	},
	{
		title: "Driver",
		key: "driver",
		render: (_, item) => item.driver?.name ?? "-",
	},
	{
		title: "Truck",
		key: "truck",
		render: (_, item) => item.truck?.number ?? "-",
	},
	{
		title: "Changed At",
		key: "createdAt",
		render: (_, item) => (item.createdAt ? formatDate(item.createdAt) : "-"),
	},
	{
		title: "Changed By",
		key: "createdBy",
		render: (_, item) => item.createdBy?.name ?? "-",
	},
];

// Adds a member to a group (memberIndex null) or edits the member at memberIndex
const DriverShiftGroupMemberModal = ({
	open,
	onClose,
	onSuccess,
	shift,
	plant,
	group,
	memberIndex,
	usedDrivers = [],
	usedTrucks = [],
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [apiNotification, contextHolder] = notification.useNotification();

	const isEdit = memberIndex !== null && memberIndex !== undefined;
	const member = isEdit ? group?.members[memberIndex] : null;
	const shiftDate = shift ? dayjs(shift.rawDate) : null;

	// Members saved before the history existed only have their creation
	const memberHistory = !member
		? []
		: member.history?.length > 0
		? member.history
		: [
				{
					status: "Created",
					startDate: member.startDate,
					endDate: member.endDate,
					driver: member.driver,
					truck: member.truck,
					createdAt: group?.createdAt,
					createdBy: group?.createdBy,
				},
		  ];

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const { driverOptions, truckOptions, loadingDrivers, loadingTrucks } =
		useShiftGroupOptions(open, plant, openNotification);

	const onFinish = async (values) => {
		const url = api + `/shift-group/member/${group._id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.patch(url, {
				memberIndex: isEdit ? memberIndex : undefined,
				timezoneOffset: new Date().getTimezoneOffset(),
				member: {
					startDate: values.startDate.toDate(),
					endDate: values.endDate.toDate(),
					driver: values.driver,
					truck: values.truck,
				},
			});
			if (data.success) {
				form.resetFields();
				// Driver whose truck or schedule change SMS couldn't be sent
				const failed = data.payload?.sms?.failed || [];
				onSuccess(
					data.message,
					failed.length > 0
						? `Skipped: ${failed
								.map((item) => `${item.driver} (${item.reason})`)
								.join(", ")}`
						: undefined,
				);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification(
				"error",
				"Error",
				`Error while trying to ${isEdit ? "edit" : "add"} member`,
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title={isEdit ? "Edit Member" : "Add Member"}
			open={open}
			onCancel={onClose}
			centered
			width={1000}
			destroyOnClose
			maskClosable={false}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onFinish={onFinish}
				preserve={false}
			>
				<Text style={{ display: "block", marginBottom: 16 }}>
					<Text strong>Group:</Text> {group?.name}
				</Text>
				<Row gutter={16}>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="startDate"
							label="Shift Start"
							initialValue={member ? dayjs(member.startDate) : undefined}
							rules={[
								{ required: true, message: "Shift start is required" },
								{
									validator: (_, value) =>
										!value || !shiftDate || !value.isBefore(shiftDate, "minute")
											? Promise.resolve()
											: Promise.reject(
													new Error(
														"Shift start can't be earlier than the shift date",
													),
											  ),
								},
							]}
						>
							<DatePicker
								size="large"
								showTime={{ format: "hh:mm A" }}
								format="MM/DD/YYYY hh:mm A"
								disabledDate={getDisabledDate(shiftDate)}
								disabledTime={getDisabledTime(shiftDate, false)}
								onChange={() =>
									form.validateFields(["endDate"]).catch(() => {})
								}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item noStyle dependencies={["startDate"]}>
							{({ getFieldValue }) => {
								const startDate = getFieldValue("startDate");
								return (
									<Form.Item
										name="endDate"
										label="Shift End"
										initialValue={member ? dayjs(member.endDate) : undefined}
										rules={[
											{ required: true, message: "Shift end is required" },
											{
												validator: (_, value) =>
													!value || !startDate || value.isAfter(startDate, "minute")
														? Promise.resolve()
														: Promise.reject(
																new Error(
																	"Shift end must be later than shift start",
																),
														  ),
											},
										]}
									>
										<DatePicker
											size="large"
											showTime={{ format: "hh:mm A" }}
											format="MM/DD/YYYY hh:mm A"
											disabled={!startDate}
											disabledDate={getDisabledDate(startDate)}
											disabledTime={getDisabledTime(startDate, true)}
											style={{ width: "100%" }}
										/>
									</Form.Item>
								);
							}}
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="driver"
							label="Driver"
							initialValue={member?.driver?._id}
							rules={[{ required: true, message: "Driver is required" }]}
						>
							<Select
								showSearch
								placeholder="Select driver"
								size="large"
								options={getAvailableOptions(driverOptions, usedDrivers)}
								filterOption={filterOption}
								loading={loadingDrivers}
								disabled={loadingDrivers}
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="truck"
							label="Truck"
							initialValue={member?.truck?._id}
							rules={[{ required: true, message: "Truck is required" }]}
						>
							<Select
								showSearch
								placeholder="Select truck"
								size="large"
								options={getAvailableOptions(truckOptions, usedTrucks)}
								filterOption={filterOption}
								loading={loadingTrucks}
								disabled={loadingTrucks}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			{isEdit && (
				<>
					<Divider orientation="left">History</Divider>
					<Table
						columns={historyColumns}
						dataSource={memberHistory.map((item, index) => ({
							...item,
							key: index,
						}))}
						pagination={false}
						size="small"
						scroll={{ x: true }}
					/>
				</>
			)}
		</Modal>
	);
};

export default DriverShiftGroupMemberModal;
